import {
  getRCAuthToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
} from '@whitelabel/cp-shared/helpers/auth';
import { claimsPortalAPI } from '@whitelabel/cp-shared/store/claimsPortalAPI';
import { parseJwt } from '@whitelabel/rentalcover-shared/helpers/utility';
import { getCPAPIHost } from '@whitelabel/cp-shared/helpers/api';
import { CP_ROOT } from '@whitelabel/cp-shared/helpers/constants';
import { setAuthToken } from '@whitelabel/rentalcover-shared/helpers/auth';

const AUTH0_ISSUER = process.env.VITE_AUTH0_ISSUER;

const isAuth0Token = (token: string): boolean => {
  try {
    if (!AUTH0_ISSUER) return false;
    const payload = parseJwt(token);
    return payload.iss === AUTH0_ISSUER;
  } catch {
    return false;
  }
};

export const authAPI = claimsPortalAPI.injectEndpoints({
  endpoints: (builder) => ({
    getTokenStatus: builder.query<string, void>({
      queryFn: async () => {
        /**
         * Get the status of the RC token
         * - expired: if the token exists but is expired
         * - valid: if the token is valid
         * - none: if the token does not exist
         */
        const token = getRCAuthToken();
        if (!token) return { data: 'none' };
        const jwt = parseJwt(token);
        const jwtExpiryDate = new Date(jwt.exp * 1000);
        const isExpired = jwtExpiryDate.getTime() < Date.now();

        return { data: isExpired ? 'expired' : 'valid' };
      },
      extraOptions: {
        isPublic: true,
      },
      providesTags: ['RCToken'],
    }),
    getRefreshToken: builder.mutation<void, void>({
      queryFn: async () => {
        const currentToken = getRCAuthToken();

        if (currentToken && isAuth0Token(currentToken)) {
          const refreshToken = getRefreshToken();
          if (!refreshToken) {
            return { error: { status: 401, data: 'No refresh token available' } };
          }

          const response = await fetch(`${getCPAPIHost()}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CP-ROOT': CP_ROOT,
              'X-Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (!response.ok) {
            clearRefreshToken();
            return { error: { status: response.status, data: response.statusText } };
          }

          const { access_token: newAccessToken, refresh_token: newRefreshToken } = await response.json();
          setAuthToken(newAccessToken);
          setRefreshToken(newRefreshToken);
          return { data: undefined };
        }

        // Legacy flow: GET /auth/refresh via claims-portal-backend
        const response = await fetch(`${getCPAPIHost()}/auth/refresh`, {
          headers: {
            'Cache-Control': 'no-store,no-cache',
            'X-CP-ROOT': CP_ROOT,
            'X-Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
          },
        });

        if (!response.ok) {
          return { error: { status: response.status, data: response.statusText } };
        }

        const legacyData = await response.json();
        setAuthToken(legacyData.jwt);
        return { data: undefined };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        // manual invalidate tags after setting the new token
        dispatch(claimsPortalAPI.util.invalidateTags(['RCToken']));
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetTokenStatusQuery, useGetRefreshTokenMutation } = authAPI;
